import { useRef, useState, type ChangeEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type {
  BulkImportValidationFailure,
  ImportBrandResponse,
  ImportBrandsBulkResponse,
  ImportValidationIssue,
  ImportWarning,
} from '@fametrue/shared';
import { useErrorMessage } from '../lib/useErrorMessage';
import { useCompanies, useImportBrand, useImportBrandsBulk, type ImportOutcome } from './api';
import { DownloadMenu } from './DownloadMenu';

/**
 * Import di un JSON brand dal pannello di amministrazione.
 *
 * L'unità di caricamento è **un oggetto brand**, non il file dell'intero
 * dataset. Il pannello accetta comunque un file con `{brands:[…]}` e chiede
 * quale brand caricare, perché è la forma in cui i dati vengono prodotti.
 *
 * Con più brand nel file compare anche "Carica tutti": il server li valida e li
 * scrive uno per uno nella stessa transazione, ma senza company — il file non
 * la contiene e non ne esiste una per tutti. Per questo il primo invio si ferma
 * sempre sull'avviso `bulk_company_not_assignable`, nello stesso flusso di
 * conferma degli altri avvisi.
 *
 * I due invii sono **entrambi primari**: con un file da dieci brand il colore
 * su un solo pulsante indicava l'azione sbagliata (feedback 23/10, punto 14).
 * A distinguerli è l'etichetta, non l'enfasi: quella del caricamento singolo
 * porta il nome del brand selezionato ("Carica Assicura Più").
 */
export function ImportPanel(): ReactNode {
  const { t } = useTranslation();
  const companies = useCompanies();
  const importBrand = useImportBrand();
  const importAll = useImportBrandsBulk();
  const describeError = useErrorMessage();
  const fileInput = useRef<HTMLInputElement>(null);

  const [raw, setRaw] = useState('');
  const [parseError, setParseError] = useState<string | null>(null);
  const [candidates, setCandidates] = useState<{ id: string; name: string; object: unknown }[]>([]);
  const [selectedBrand, setSelectedBrand] = useState<string>('');
  const [companyId, setCompanyId] = useState<string>('');
  const [outcome, setOutcome] = useState<ImportOutcome | null>(null);
  /** Quale dei due invii ha prodotto l'esito corrente: la conferma ripete quello. */
  const [mode, setMode] = useState<'single' | 'bulk'>('single');

  function parseInput(text: string): void {
    setRaw(text);
    setOutcome(null);
    setParseError(null);
    setCandidates([]);
    setSelectedBrand('');

    if (text.trim().length === 0) return;

    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch (error) {
      setParseError(error instanceof Error ? error.message : String(error));
      return;
    }

    // Tre forme accettate: oggetto brand singolo, { brand: {...} },
    // oppure il file completo { brands: [...] } da cui scegliere.
    const found = extractBrands(parsed);
    if (found.length === 0) {
      setParseError(t('admin.import.noBrandFound'));
      return;
    }
    setCandidates(found);
    setSelectedBrand(found[0]?.id ?? '');
  }

  function onFile(event: ChangeEvent<HTMLInputElement>): void {
    const file = event.target.files?.[0];
    if (!file) return;
    void file.text().then(parseInput);
  }

  const selected = candidates.find((c) => c.id === selectedBrand);

  const pending = importBrand.isPending || importAll.isPending;

  function submit(acknowledgeWarnings: boolean): void {
    if (!selected) return;
    setMode('single');
    importBrand.mutate(
      {
        brand: selected.object,
        companyId: companyId || undefined,
        acknowledgeWarnings,
      },
      { onSuccess: setOutcome },
    );
  }

  function submitAll(acknowledgeWarnings: boolean): void {
    if (candidates.length < 2) return;
    setMode('bulk');
    importAll.mutate(
      { brands: candidates.map((c) => c.object), acknowledgeWarnings },
      { onSuccess: setOutcome },
    );
  }

  function confirm(): void {
    if (mode === 'bulk') submitAll(true);
    else submit(true);
  }

  function reset(): void {
    setRaw('');
    setCandidates([]);
    setSelectedBrand('');
    setOutcome(null);
    setParseError(null);
    if (fileInput.current) fileInput.current.value = '';
  }

  return (
    <section className="admin-card">
      <header className="admin-card-head admin-card-head-row">
        <div>
          <h2>{t('admin.import.title')}</h2>
          <p>{t('admin.import.subtitle')}</p>
        </div>
        <DownloadMenu />
      </header>

      <div className="admin-field">
        <label htmlFor="import-company">{t('admin.import.company')}</label>
        <select
          id="import-company"
          value={companyId}
          onChange={(e) => setCompanyId(e.target.value)}
          disabled={companies.isPending}
        >
          <option value="">{t('admin.import.noCompany')}</option>
          {(companies.data ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <p className="admin-hint">{t('admin.import.companyHint')}</p>
      </div>

      <div className="admin-field">
        <label htmlFor="import-file">{t('admin.import.file')}</label>
        <input
          id="import-file"
          type="file"
          accept="application/json,.json"
          ref={fileInput}
          onChange={onFile}
        />
      </div>

      <div className="admin-field">
        <label htmlFor="import-json">{t('admin.import.paste')}</label>
        <textarea
          id="import-json"
          rows={8}
          spellCheck={false}
          value={raw}
          placeholder='{ "id": "brand-esempio", "name": "Brand Esempio", ... }'
          onChange={(e) => parseInput(e.target.value)}
        />
      </div>

      {parseError ? (
        <p className="admin-alert error">
          {t('admin.import.jsonInvalid')}: {parseError}
        </p>
      ) : null}

      {candidates.length > 1 ? (
        <div className="admin-field">
          <label htmlFor="import-brand">{t('admin.import.whichBrand')}</label>
          <select
            id="import-brand"
            value={selectedBrand}
            onChange={(e) => {
              setSelectedBrand(e.target.value);
              setOutcome(null);
            }}
          >
            {candidates.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} ({c.id})
              </option>
            ))}
          </select>
          <p className="admin-hint">
            {t('admin.import.whichBrandHint', { count: candidates.length })}
          </p>
        </div>
      ) : null}

      {selected ? (
        <p className="admin-selected">
          {t('admin.import.readyToImport')} <strong>{selected.name}</strong>{' '}
          <code>{selected.id}</code>
        </p>
      ) : null}

      <div className="admin-actions">
        <button
          type="button"
          className="admin-btn primary"
          disabled={!selected || pending}
          onClick={() => submit(false)}
        >
          {importBrand.isPending
            ? t('admin.import.importing')
            : candidates.length > 1 && selected
              ? t('admin.import.submitOne', { brand: selected.name })
              : t('admin.import.submit')}
        </button>
        {candidates.length > 1 ? (
          <button
            type="button"
            className="admin-btn primary"
            disabled={pending}
            onClick={() => submitAll(false)}
          >
            {importAll.isPending
              ? t('admin.import.importing')
              : t('admin.import.submitAll', { count: candidates.length })}
          </button>
        ) : null}
        <button type="button" className="admin-btn" onClick={reset}>
          {t('admin.import.reset')}
        </button>
      </div>

      {outcome ? (
        <ImportOutcomeView
          outcome={outcome}
          bulk={mode === 'bulk'}
          onConfirm={confirm}
          confirming={pending}
          describeError={describeError}
        />
      ) : null}
    </section>
  );
}

/** Estrae i brand caricabili dalle tre forme di input accettate. */
function extractBrands(parsed: unknown): { id: string; name: string; object: unknown }[] {
  if (!parsed || typeof parsed !== 'object') return [];
  const obj = parsed as Record<string, unknown>;

  if (Array.isArray(obj['brands'])) {
    return obj['brands']
      .filter((b): b is Record<string, unknown> => Boolean(b) && typeof b === 'object')
      .map((b) => ({
        id: typeof b['id'] === 'string' ? b['id'] : '?',
        name: typeof b['name'] === 'string' ? b['name'] : '?',
        object: b,
      }));
  }

  const single = (obj['brand'] ?? obj) as Record<string, unknown>;
  if (typeof single['id'] !== 'string') return [];
  return [
    {
      id: single['id'],
      name: typeof single['name'] === 'string' ? single['name'] : single['id'],
      object: single,
    },
  ];
}

function ImportOutcomeView({
  outcome,
  bulk,
  onConfirm,
  confirming,
  describeError,
}: {
  outcome: ImportOutcome;
  bulk: boolean;
  onConfirm: () => void;
  confirming: boolean;
  describeError: (error: unknown) => string | null;
}): ReactNode {
  const { t } = useTranslation();

  if (outcome.kind === 'ok') return <ImportSuccess result={outcome.result} />;
  if (outcome.kind === 'okBulk') return <BulkImportSuccess result={outcome.result} />;

  if (outcome.kind === 'invalid') {
    return (
      <div className="admin-alert error">
        <strong>{t('admin.import.rejected')}</strong>
        <p>{t('admin.import.rejectedHint')}</p>
        <IssueList issues={outcome.issues} />
      </div>
    );
  }

  if (outcome.kind === 'invalidBulk') return <BulkFailures failures={outcome.failures} />;

  if (outcome.kind === 'needsConfirmation') {
    return (
      <div className="admin-alert warning">
        <strong>{t('admin.import.warnings')}</strong>
        <ul className="admin-list">
          {outcome.warnings.map((w, i) => (
            <li key={`${w.brandId ?? ''}-${w.code}-${i}`}>
              <WarningText warning={w} />
            </li>
          ))}
        </ul>
        <p>{bulk ? t('admin.import.warningsHintBulk') : t('admin.import.warningsHint')}</p>
        <button
          type="button"
          className="admin-btn primary"
          disabled={confirming}
          onClick={onConfirm}
        >
          {confirming ? t('admin.import.importing') : t('admin.import.confirm')}
        </button>
      </div>
    );
  }

  return <p className="admin-alert error">{describeError(outcome.error)}</p>;
}

/**
 * Separa il codice della regola dall'eventuale dettaglio in coda
 * ("stars_somma_non_100 (11200.00)", "piattaforma_duplicata: trustpilot").
 * I messaggi di Zod sui tipi di base non hanno questa forma e restano intatti:
 * sono diagnostica tecnica per chi corregge il file, non testo per il cliente.
 */
function splitIssue(message: string): { code: string; detail: string | undefined } | null {
  const match = /^([a-z0-9_]+)(\s*[:(].*)?$/.exec(message);
  if (!match?.[1]) return null;
  return { code: match[1], detail: match[2]?.trim() };
}

/**
 * Testo mostrato per un errore di validazione, in tre passaggi.
 *
 * 1. **Regole scritte da noi**: il messaggio è già un codice snake_case
 *    (`nd_non_propagato`), tradotto da `admin.issues.*` con l'eventuale coda
 *    tecnica che il validatore aggiunge fra parentesi.
 * 2. **Errori di forma di Zod**: il messaggio nativo è inglese in entrambe le
 *    lingue, quindi si compone da `code` e `params` via `admin.zodIssues.*`.
 *    I nomi dei tipi passano a loro volta da `admin.zodTypes.*`, altrimenti
 *    metà della frase resterebbe in inglese.
 * 3. **Ultima spiaggia**: il messaggio così com'è. Meglio una riga tecnica che
 *    una riga vuota per chi sta cercando di correggere un file.
 */
function issueText(issue: ImportValidationIssue, t: TFunction): string {
  const nostro = splitIssue(issue.message);
  if (nostro) {
    const tradotto = t(`admin.issues.${nostro.code}`, { defaultValue: '' });
    if (tradotto) return nostro.detail ? `${tradotto} ${nostro.detail}` : tradotto;
  }

  if (!issue.code) return issue.message;

  const params = issue.params ?? {};
  const tipo = (name: string | number | undefined): string =>
    name === undefined ? '' : t(`admin.zodTypes.${name}`, { defaultValue: String(name) });

  // `too_big` e `too_small` cambiano frase a seconda di che cosa è troppo: i
  // caratteri di un testo, gli elementi di un elenco o il valore di un numero.
  const chiavi = [
    params['origin'] !== undefined
      ? `admin.zodIssues.${issue.code}.${String(params['origin'])}`
      : `admin.zodIssues.${issue.code}`,
    `admin.zodIssues.${issue.code}.default`,
    `admin.zodIssues.${issue.code}`,
    'admin.zodIssues.default',
  ];

  return t(chiavi, {
    ...params,
    expected: tipo(params['expected']),
    received: tipo(params['received']),
    defaultValue: issue.message,
  });
}

/**
 * Nel caricamento in blocco l'avviso porta il brand a cui si riferisce; quello
 * sul mancato aggancio alla company vale per tutto il file e non ne ha uno.
 */
function WarningText({ warning }: { warning: ImportWarning }): ReactNode {
  const { t } = useTranslation();
  const text = t(`admin.warnings.${warning.code}`, {
    ...warning.context,
    defaultValue: warning.code,
  });
  if (!warning.brandId) return text;
  return (
    <>
      <code>{warning.brandId}</code> {text}
    </>
  );
}

function BulkImportSuccess({ result }: { result: ImportBrandsBulkResponse }): ReactNode {
  const { t } = useTranslation();
  const created = result.brands.filter((b) => b.created).length;
  return (
    <div className="admin-alert success">
      <strong>{t('admin.import.bulkDone', { count: result.brands.length })}</strong>
      <p>
        {t('admin.import.bulkDoneDetail', {
          created,
          replaced: result.brands.length - created,
        })}
      </p>
      <ul className="admin-list">
        {result.brands.map(({ brand }) => (
          <li key={brand.id}>
            {brand.name} <code>{brand.slug}</code> · {brand.sectorKey}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** 422 del caricamento in blocco: gli errori, brand per brand. Nulla è stato scritto. */
function BulkFailures({ failures }: { failures: BulkImportValidationFailure[] }): ReactNode {
  const { t } = useTranslation();
  return (
    <div className="admin-alert error">
      <strong>{t('admin.import.rejectedBulk', { count: failures.length })}</strong>
      <p>{t('admin.import.rejectedHint')}</p>
      {failures.map((f) => (
        <div key={f.index} className="admin-bulk-failure">
          <p className="admin-selected">
            <strong>{f.brandId ?? t('admin.import.brandAtIndex', { index: f.index + 1 })}</strong>
          </p>
          <IssueList issues={f.issues} />
        </div>
      ))}
    </div>
  );
}

function ImportSuccess({ result }: { result: ImportBrandResponse }): ReactNode {
  const { t } = useTranslation();
  return (
    <div className="admin-alert success">
      <strong>
        {result.created ? t('admin.import.created') : t('admin.import.replaced')}:{' '}
        {result.brand.name}
      </strong>
      <p>
        <code>{result.brand.slug}</code> · {result.brand.sectorKey}
      </p>
    </div>
  );
}

function IssueList({ issues }: { issues: ImportValidationIssue[] }): ReactNode {
  const { t } = useTranslation();
  // Elenco lungo: si mostrano le prime, il resto è rumore per chi corregge.
  const shown = issues.slice(0, 25);

  return (
    <>
      <ul className="admin-issues">
        {shown.map((issue, i) => (
          <li key={`${issue.path}-${i}`}>
            <code>{issue.path || t('admin.import.rootPath')}</code>
            <span>{issueText(issue, t)}</span>
          </li>
        ))}
      </ul>
      {issues.length > shown.length ? (
        <p className="admin-hint">
          {t('admin.import.moreIssues', { count: issues.length - shown.length })}
        </p>
      ) : null}
    </>
  );
}
